import {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
    useRef,
} from 'react'
import Cookies from 'js-cookie'
import {
    InitiateAuthCommandOutput,
    SignUpCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider'
import { createWrapper } from 'components/LogicalWrapperFactory'
import { ModalDataAttributes, UserModel } from 'components/Modals/types'
import useModal from 'components/Modals/useModal'
import forgotPassword, {
    confirmForgotPassword,
} from 'services/cognito/forgot-password'
import login from 'services/cognito/login'
import logout from 'services/cognito/logout'
import profile from 'services/cognito/profile'
import cognitoSignUp from 'services/cognito/signup'
import { AuthContextType, AuthHeaderTenantUserType, SkyUser } from './types'
import { postApiRequest, useApiRequest, useFetch } from 'utils/fetch-helper'
import { ClientApiOutput, ClientApiPostRequest } from 'types/client'
import hasValidAccessToken from 'services/cognito/refresh-token'
import { useTenantContext } from './TenantContext'
import { Tenant } from 'types/tenant'
import { RoleTypes, StaffResponseItem } from 'types/staff'
import { FetchMethods } from 'utils/types'
import { useRouter } from 'next/router'
import { useAppErrorApiContext } from './AppErrorContext'
import { createOrLinkUser, createOrUpdate } from 'services/client'
import getConfig from 'next/config'

export const default_primary_color = '#183283'
export const default_border_color = '#D1D5DB'
export const tenant_logo_url = '/images/aot-logo-symbol.svg'

export interface UserProfileRecord {
    user?: UserModel
    roles?: string[]
    tenants?: Tenant[]
    client_id?: string
    user_type?: string
}

type ApiUser = {
    userInfo: UserModel
    roles: string[]
    tenants: Tenant[]
    clients: ClientApiOutput[]
    staff: StaffResponseItem[]
    userType: string
    cognitoId: string
}

export const AuthContext = createContext<AuthContextType>(
    {} as AuthContextType
)

export function useAuth() {
    return useContext(AuthContext)
}

export const Authenticated = createWrapper(
    AuthContext,
    (ctx) => !!ctx.user?.uuid
)
export const Activated = createWrapper(
    AuthContext,
    (ctx) => !!ctx.user?.phone
)
export const NotAuthenticated = createWrapper(
    AuthContext,
    (ctx) => !ctx.user?.uuid
)

export function SkyAuthProvider({ children }: { children: ReactNode }) {
    const router = useRouter()
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const [user, setUser] = useState<SkyUser>({} as unknown as SkyUser)
    const { tenant, setTenant, setActiveTenantObj } = useTenantContext()
    const [role, setRole] = useState<string>()
    const [roles, setRoles] = useState<RoleTypes[]>([])
    const [tenants, setTenants] = useState<Tenant[]>(
        [] as unknown as Tenant[]
    )
    const [already_set, setRecordsRetrievalStatus] = useState<
        Record<string, boolean>
    >({})
    const [is_drawer_expanded, toggleDrawerSize] = useState<boolean>(
        Cookies.get('drawer') == 'expanded'
    )
    const [user_profile, setUserProfile] = useState<UserProfileRecord>({})
    const [attributes, setAttributes] = useState<ModalDataAttributes>({})

    function hasRole(role_types: RoleTypes[]): boolean {
        return role_types.some((role) => roles.includes(role))
    }

    const { data: tenant_list_xhr, doFetch: getTenantList } = useFetch(
        `/v1/tenants?size=1000`,
        FetchMethods.GET,
        false
    )

    const { data: is_auth_google_xhr, doFetch: getIsAuthGoogle } = useFetch(
        `/v1/google-calendar/is-authorized`,
        FetchMethods.GET,
        false
    )
    useEffect(() => {
            const urlParams = new URLSearchParams(window.location.search);
            console.log("🚀 ~ useEffect ~ urlParams:", urlParams)
            const access_token = urlParams.get("access_token") || '';
            const id_token = urlParams.get("id_token");
            const refresh_token = urlParams.get("refresh_token");
            const ExpiresIn = 3600; // 1 hour in seconds
            if(access_token) {
                Cookies.set('access_token', access_token, {
                    path: '/',
                    expires: Number(ExpiresIn),
                    sameSite: 'None',
                    secure: true,
                })
            }
            if (id_token) {
                Cookies.set('id_token', id_token, {
                    path: '/',
                    expires: Number(ExpiresIn),
                    sameSite: 'None',
                    secure: true,
                })
            }
            if (refresh_token) {
                Cookies.set('refresh_token', refresh_token, {
                    path: '/',
                    sameSite: 'None',
                    secure: true,
                })
            }
    }, [])
       
    useEffect(() => {
        if (hasRole([RoleTypes.SUPER_ADMIN])) {
            getTenantList()
        }
    }, [roles])

    const LoginModal = useModal()
    const SignupModal = useModal()
    const ClientLoginModal = useModal()
    const ClientSignupModal = useModal()
    const EmailVerificationModal = useModal()
    const BookingModal = useModal()
    const HtmlPreviewModal = useModal()
    const PrintPreviewModal = useModal()
    const CalendarPrintModal = useModal()
    const TenantModal = useModal()
    const CreateClientModal = useModal()
    const GeneralModal = useModal()
    const LocationModal = useModal()
    const ServiceModal = useModal()
    const StaffModal = useModal()
    const StaffConnectCalendarModal = useModal()
    const UpsellModal = useModal()
    const MembershipModal = useModal()
    const CreditCardModal = useModal()
    const PaymentModal = useModal()
    const GlobalDialog = useModal()
    const ClassModal = useModal()
    const AppointmentAddModal = useModal()
    const MembershipAddModal = useModal()
    const MembershipEditModal = useModal()
    const OrderEditModal = useModal()
    const PurchaseSummaryModal = useModal()
    const GlobalSchedulingLimitsModal = useModal()
    const CreateCouponModal = useModal()
    const CustomBookingPageModal = useModal()
    const POSModal = useModal()

    async function getProfile() {
        const auth_data = await profile()
        if (auth_data && auth_data.uuid) {
            if (
                auth_data.user_type == 'CLIENT' &&
                router.pathname.indexOf('/dashboard') > -1
            ) {
                router.replace({
                    pathname: '/book/client/appointments/',
                    query: router.query
                })
            }

            setUser({
                first_name: auth_data.given_name,
                last_name: auth_data.family_name,
                uuid: auth_data.uuid,
                email: auth_data.email,
            })

            let headers: AuthHeaderTenantUserType = {}
            if (auth_data.user_type == 'CLIENT') {
                const tenant_id = router.query.t as string || tenant?.id || auth_data.tenant_id
                if (!tenant_id) return
                headers = {
                    'tenant-id': tenant_id,
                    'user-type': auth_data.user_type || 'TENANT'
                }
            }

            const res: ApiUser & {
                status?: number
                error?: string
                ok?: boolean
            } = await getApiRequest('/v1/users/current', headers)
            setActiveTenantObj(res)
            if (res.status && res.ok === false) {
                if (tenant?.id && user) {
                    const clientDto: ClientApiPostRequest = {
                        cognitoId: auth_data.uuid,
                        tenant,
                        user: {
                            firstName: auth_data.given_name,
                            lastName: auth_data.family_name,
                            email: auth_data.email,
                            phone: '',
                            timezone: 'Asia/Singapore'
                        },
                    }
                    createOrLinkUser(clientDto)
                    getProfile()
                }
                else {
                    console.log('API ERROR /v1/users/current')
                    console.error(res)
                    setGlobalError({ message: 'Failed to retrieve user data' })
                }
            } else {
                console.log("user_profile Timezone", res.userInfo.timezone);
                Cookies.set('timezone', res.userInfo.timezone || 'Asia/Singapore');
                initUserProfile(res)
            }
        } else {
            // TODO: This should show the login modal
            console.log('Cognito get profile failed')
            if (router.pathname === "/kiosk") {
                window.history.replaceState({ ...window.history.state, as: "/login", url: "/login" }, '', "/login")
                    LoginModal.open()
            }
        }
    }

    const value = {
        attributes,
        setAttributes,
        user,
        setUser,
        user_profile,
        setUserProfile,
        role,
        is_drawer_expanded,
        toggleDrawerSize,
        login: async (
            email: string,
            password: string
        ): Promise<InitiateAuthCommandOutput> => {
            const result: InitiateAuthCommandOutput = await login({
                email,
                password,
            })

            const { AuthenticationResult } = result

            if (AuthenticationResult) {
                const { IdToken, AccessToken, ExpiresIn, RefreshToken } =
                    AuthenticationResult
                if (AccessToken) {
                    Cookies.set('email', email, {
                        path: '/',
                        expires: Number(ExpiresIn),
                        sameSite: 'None',
                        secure: true,
                    })
                    Cookies.set('access_token', AccessToken, {
                        path: '/',
                        expires: Number(ExpiresIn),
                        sameSite: 'None',
                        secure: true,
                    })
                    if (IdToken) {
                        Cookies.set('id_token', IdToken, {
                            path: '/',
                            expires: Number(ExpiresIn),
                            sameSite: 'None',
                            secure: true,
                        })
                    }
                    if (RefreshToken) {
                        Cookies.set('refresh_token', RefreshToken, {
                            path: '/',
                            sameSite: 'None',
                            secure: true,
                        })
                    }

                    getProfile()
                }
            }
            return result
        },
        confirmForgotPassword: async (
            email: string,
            new_password: string,
            code: string
        ) => {
            // const res = await getApiRequest(`/v1/cognito/check_pass_code_availability?email=${email}&passCode=${code}`)
            // console.log("Password Validity", res);
            // if (res.message === 'Valid passcode.') {
            const res = await confirmForgotPassword(
                email,
                new_password,
                code
            )
            return res
            // }
        },
        forgotPassword: async (email: string) => {
            const x = await forgotPassword({ email })
        },
        logout: async () => {
            await logout()
            const { API_ENDPOINT, LANDING_PAGES_HOST } = getConfig().publicRuntimeConfig
            if (API_ENDPOINT.includes('prod')) {
                document.location = LANDING_PAGES_HOST
            } else {
                document.location.href = '/'
            }
        },
        profile,
        signup: async (
            email: string,
            password: string,
            given_name: string,
            family_name: string,
            user_type: 'TENANT' | 'CLIENT' = 'TENANT',
            tenant_id: string = ''
        ): Promise<SignUpCommandOutput | boolean> => {
            const output: SignUpCommandOutput = await cognitoSignUp({
                email,
                password,
                given_name,
                family_name,
                user_type,
                tenant_id,
            })

            return output || false
        },
        hasRole,
        is_auth_google: is_auth_google_xhr,
        getIsAuthGoogle,
        tenants,
        tenant_switch_list: tenant_list_xhr,
        getTenantList,
        BookingModal,
        CreateClientModal,
        CreditCardModal,
        GeneralModal,
        LocationModal,
        LoginModal,
        ClientLoginModal,
        HtmlPreviewModal,
        PrintPreviewModal,
        CalendarPrintModal,
        ServiceModal,
        StaffModal,
        StaffConnectCalendarModal,
        UpsellModal,
        SignupModal,
        ClientSignupModal,
        EmailVerificationModal,
        TenantModal,
        MembershipModal,
        POSModal,
        PaymentModal,
        GlobalDialog,
        ClassModal,
        AppointmentAddModal,
        MembershipAddModal,
        MembershipEditModal,
        OrderEditModal,
        PurchaseSummaryModal,
        GlobalSchedulingLimitsModal,
        CustomBookingPageModal,
        CreateCouponModal,
    }

    const { all_tenants, active_tenant } = already_set

    function initUserProfile(res: ApiUser) {
        if (!res) {
            return
        }
        const user_record: UserProfileRecord = {
            user: res.userInfo,
            roles: res.roles,
            tenants: [],
            user_type: res.userType,
        }

        function addTenants(tenants: Tenant[]) {
            let show_onboarding = false
            tenants &&
                tenants.forEach((t: Tenant, idx) => {
                    user_record.tenants?.push(t)

                    if (t?.id && t.id !== tenant?.id) {
                        if (
                            Cookies.get('tenant_id') &&
                            t.id == Cookies.get('tenant_id') &&
                            !Cookies.get('tenant_switch')
                        ) {
                            setTenant(t)
                            show_onboarding = !t.onboardingStatus
                        } else if (
                            idx == 0 &&
                            !Cookies.get('tenant_switch')
                        ) {
                            setTenant(t)
                            Cookies.set('tenant_id', t.id, {
                                sameSite: 'None',
                                secure: true,
                            })
                            show_onboarding = !t.onboardingStatus
                        }
                    }
                })
            if (router.pathname.includes("/login") || router.pathname.includes("/welcome-aboard") || router.pathname == "/") {
                // document.body.style.fontFamily = "'Poppins', sans-serif";
                if (show_onboarding) {
                    router.push('/get_started/')
                } else {
                    router.push('/dashboard/calendar/')
                }
            }
        }

        // Client User
        if (res.clients) {
            user_record.client_id = res.clients[0].id
            let t = res.clients[0].tenant
            addTenants([t])
        }

        // Tenant User
        addTenants(res.tenants)
        addTenants(
            res.staff
                ?.filter((staff) => !!staff.tenant)
                .map((staff) => staff.tenant as Tenant)
        )

        setUserProfile(user_record)
        setUser((prev_user) => ({
            ...prev_user,
            id: res.userInfo?.id,
        }))
        setRole(res?.roles[0])
        setRoles(res.roles.map((role) => role as RoleTypes))
    }

    useEffect(() => {
        if (is_drawer_expanded) {
            Cookies.set('drawer', 'expanded', {
                sameSite: 'None',
                secure: true,
            })
        } else {
            Cookies.set('drawer', 'mini', {
                sameSite: 'None',
                secure: true,
            })
        }
        const { tenants: api_tenants } = user_profile

        if (LoginModal.is_open && user.uuid && Cookies.get('id_token')) {
            LoginModal.close()
        }

        if (user_profile.user && !all_tenants && !active_tenant) {
            const records: Tenant[] = []
            api_tenants?.forEach((t: Tenant) => {
                records.push(t)
                if (!active_tenant) {
                    if (
                        t.id == Cookies.get('tenant_id') &&
                        t.id !== tenant?.id &&
                        !Cookies.get('tenant_switch')
                    ) {
                        setTenant(t)
                        setRecordsRetrievalStatus({
                            ...already_set,
                            active_tenant: true,
                        })
                    }
                }

                if (
                    api_tenants &&
                    records.length >= api_tenants.length &&
                    !all_tenants
                ) {
                    setTenants(records)
                    setRecordsRetrievalStatus({
                        ...already_set,
                        all_tenants: true,
                    })
                    if (!active_tenant && tenant?.id !== records[0].id) {
                        setTenant(records[0])
                    }
                }
            })
            if (LoginModal.is_open) {
                LoginModal.close()
            }
        } else if (!user || !user?.uuid) {
            getProfile()
        }
    }, [
        user,
        tenant,
        all_tenants,
        active_tenant,
        already_set,
        user_profile,
        is_drawer_expanded,
    ])

    // Refresh Token
    const intervalRef = useRef()
    useEffect(() => {
        const interval: any = setInterval(async () => {
            const has_token = await hasValidAccessToken()
            if (!has_token) {
                // window.location.reload()
            }
        }, 1000 * 60 * 5) // check every 5 minutes
        intervalRef.current = interval
        return () => clearInterval(interval)
    }, [hasValidAccessToken])

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export async function getStaticProps() {
    return {
        props: {
            region: process.env.COGNITO_REGION,
            client_id: process.env.COGNITO_CLIENT_ID,
        },
    }
}
